import is from '@sindresorhus/is';
import {
    type ChangeEvent,
    type FunctionComponent,
    type KeyboardEvent,
    type PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { MAIN_TILE_ID } from '~/ui/domain/tiling-engine/constants';
import { useTilingEngineContext } from '~/ui/domain/tiling-engine/TilingEngineContext';

import {
    type ChatComposerActive,
    ChatComposerActiveContext,
    type ChatComposerCallbacks,
    ChatComposerCallbacksContext,
    ChatComposerConversationContext,
    type ChatComposerInput,
    ChatComposerInputContext,
    useChatComposerActive,
    useChatComposerCallbacks,
    useChatComposerInput,
} from '../chat-composer-contexts';
import { chatContextsToFrontendContext } from '../chat-context-to-frontend-context';
import type { ChatContext } from '../chat-context-types';
import { CHAT_TILE_ID } from '../constants';
import { useChatContextRegistry } from '../hooks/use-chat-context-registry';
import { useChatConversation } from '../hooks/use-chat-conversation';
import { useChatTranscription } from '../hooks/use-chat-transcription';
import { useDeferredFlag } from '../hooks/use-deferred-flag';
import { useChatOpen, useChatStore } from '../stores/chat-open-store';

export {
    useChatComposerActive,
    useChatComposerCallbacks,
    useChatComposerConversation,
    useChatComposerInput,
} from '../chat-composer-contexts';

const COMPOSER_MOUNT_DELAY_MS = 1000;

// Matches the TilingEngine `gap-m` flex gap (→ `--spacing-m: 0.5rem`).
// Threshold for "a tile has crossed under the sticky chat dock".
const CHAT_TILE_GAP_PX = 8;

type ChatComposerProviderProps = PropsWithChildren<{
    readonly isEnabled?: boolean;
}>;

export const ChatComposerProvider: FunctionComponent<ChatComposerProviderProps> = ({ children, isEnabled = false }) => {
    const isExpanded = useChatOpen();
    const baseOpen = useChatStore((state) => state.open);
    const closeChat = useChatStore((state) => state.close);

    const [draft, setDraft] = useState('');
    const [hoveredTileId, setHoveredTileId] = useState<string | null>(null);
    const [hasComposerAppeared, setHasComposerAppeared] = useState(false);
    const [isCollapsedComposerOpen, setCollapsedComposerOpen] = useState(false);
    const [isChatScrolled, setIsChatScrolled] = useState(false);
    const [isContentUnderChat, setIsContentUnderChat] = useState(false);
    const [collapsedComposerExpandSignal, setCollapsedComposerExpandSignal] = useState(0);

    const expandCollapsedComposer = useCallback(() => {
        setCollapsedComposerExpandSignal((value) => value + 1);
    }, []);

    const isComposerReady = useDeferredFlag(COMPOSER_MOUNT_DELAY_MS);

    const markComposerAppeared = useCallback(() => setHasComposerAppeared(true), []);

    const { containerRef, registeredTiles, subscribe } = useTilingEngineContext();

    const handleTranscribedText = useCallback((text: string) => {
        setDraft((previous) => (previous.length > 0 ? `${previous} ${text}` : text));
    }, []);

    const transcription = useChatTranscription(handleTranscribedText);

    const handleDraftChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>): void => {
        setDraft(event.target.value);
    }, []);

    const conversation = useChatConversation();

    const draftRef = useRef(draft);
    draftRef.current = draft;
    const conversationSendRef = useRef(conversation.send);
    conversationSendRef.current = conversation.send;
    const openRef = useRef(baseOpen);
    openRef.current = baseOpen;
    const activeChatContextsRef = useRef<readonly ChatContext[]>([]);
    // Mirror turn status into a ref so submit/handleKeyDown stay stable while
    // still gating on the live streaming state. sendMessage stays ungated so
    // structured-renderer responses (e.g. clarify-options) keep working.
    const turnStatusRef = useRef(conversation.turn.status);
    turnStatusRef.current = conversation.turn.status;
    const startNewThreadRef = useRef(conversation.startNewThread);
    startNewThreadRef.current = conversation.startNewThread;

    // Collapsing ends the current session: drop the active thread so the next
    // expand — clicking the pill, or typing into the collapsed composer —
    // begins a fresh thread rather than resuming the previous one (which the
    // collapsed chat can't surface, e.g. a turn awaiting the user's answer).
    const close = useCallback((): void => {
        startNewThreadRef.current();
        closeChat();
    }, [closeChat]);

    const submit = useCallback((): void => {
        if (turnStatusRef.current === 'streaming') return;
        const text = draftRef.current.trim();
        if (text.length === 0) return;
        void conversationSendRef.current({
            frontendContext: chatContextsToFrontendContext(activeChatContextsRef.current, window.location.href),
            message: text,
        });
        setDraft('');
        openRef.current();
    }, []);

    const sendMessage = useCallback((text: string): void => {
        const trimmed = text.trim();
        if (trimmed.length === 0) return;
        void conversationSendRef.current({
            frontendContext: chatContextsToFrontendContext(activeChatContextsRef.current, window.location.href),
            message: trimmed,
        });
        setDraft('');
        openRef.current();
    }, []);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLTextAreaElement>): void => {
            if (event.key === 'Enter' && !event.shiftKey) {
                if (turnStatusRef.current === 'streaming') return;
                event.preventDefault();
                submit();
            }
        },
        [submit],
    );

    const livePanelIds = useMemo(
        () => registeredTiles.filter((tile) => tile.id !== CHAT_TILE_ID).map((tile) => tile.id),
        [registeredTiles],
    );

    useEffect(() => {
        if (!isExpanded) {
            setHoveredTileId(null);
        } else {
            setCollapsedComposerOpen(false);
        }
    }, [isExpanded]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: registeredTiles re-runs the effect on tile mount/unmount; ResizeObserver only catches scroller size changes, not scrollWidth changes from new siblings.
    useEffect(() => {
        if (!isExpanded) {
            setIsChatScrolled(false);
            setIsContentUnderChat(false);
            return;
        }

        const scroller = containerRef.current;

        if (is.null(scroller)) {
            return;
        }

        const measure = (): void => {
            const max = scroller.scrollWidth - scroller.clientWidth;
            const distanceFromRight = max - scroller.scrollLeft;
            const scrolled = max > 0 && distanceFromRight > 1;
            const underChat = max > 0 && distanceFromRight > CHAT_TILE_GAP_PX + 1;
            setIsChatScrolled((previous) => (previous === scrolled ? previous : scrolled));
            setIsContentUnderChat((previous) => (previous === underChat ? previous : underChat));
        };

        measure();
        scroller.addEventListener('scroll', measure, { passive: true });

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(scroller);

        window.addEventListener('resize', measure);

        // Re-measure once the engine reports the layout has settled (e.g.
        // Main's width transition completing as the chat dock takes its
        // slot). Without this the no-overflow case never re-fires `measure`
        // after Main shrinks, leaving the flags stuck at their pre-settle
        // values.
        const unsubscribe = subscribe((event) => {
            if (event.type === 'layoutSettled') {
                measure();
            }
        });

        return () => {
            scroller.removeEventListener('scroll', measure);
            resizeObserver.disconnect();
            window.removeEventListener('resize', measure);
            unsubscribe();
        };
    }, [isExpanded, containerRef, registeredTiles, subscribe]);

    const anchorTileId = useMemo(() => {
        const isHoveredAlive =
            hoveredTileId !== null && (hoveredTileId === MAIN_TILE_ID || livePanelIds.includes(hoveredTileId));

        if (isHoveredAlive) {
            return hoveredTileId;
        }

        return livePanelIds[livePanelIds.length - 1] ?? MAIN_TILE_ID;
    }, [hoveredTileId, livePanelIds]);

    const activeTileId = isExpanded ? CHAT_TILE_ID : anchorTileId;

    const { activeChatContext, contextsByTileId, registerChatContext, unregisterChatContext } =
        useChatContextRegistry(anchorTileId);

    const activeChatContexts = useMemo<readonly ChatContext[]>(() => {
        const visibleTileIds = [MAIN_TILE_ID, ...livePanelIds];

        return visibleTileIds.flatMap((tileId) => {
            const context = contextsByTileId.get(tileId);
            return is.undefined(context) ? [] : [context];
        });
    }, [contextsByTileId, livePanelIds]);

    const resolvedChatContext = activeChatContext ?? activeChatContexts[activeChatContexts.length - 1] ?? null;
    activeChatContextsRef.current = activeChatContexts;

    const callbacks = useMemo<ChatComposerCallbacks>(
        () => ({
            close,
            expandCollapsedComposer,
            handleDraftChange,
            handleKeyDown,
            hasComposerAppeared,
            isComposerEnabled: isEnabled,
            isComposerReady,
            markComposerAppeared,
            open: baseOpen,
            registerChatContext,
            sendMessage,
            setCollapsedComposerOpen,
            setHoveredTile: setHoveredTileId,
            submit,
            unregisterChatContext,
        }),
        [
            baseOpen,
            close,
            expandCollapsedComposer,
            handleDraftChange,
            handleKeyDown,
            hasComposerAppeared,
            isEnabled,
            isComposerReady,
            markComposerAppeared,
            registerChatContext,
            sendMessage,
            submit,
            unregisterChatContext,
        ],
    );

    const active = useMemo<ChatComposerActive>(
        () => ({
            activeChatContext,
            activeChatContexts,
            activeTileId,
            anchorTileId,
            collapsedComposerExpandSignal,
            isCollapsedComposerOpen,
            isChatScrolled,
            isContentUnderChat,
            isExpanded,
            resolvedChatContext,
        }),
        [
            activeChatContext,
            activeChatContexts,
            activeTileId,
            anchorTileId,
            collapsedComposerExpandSignal,
            isCollapsedComposerOpen,
            isChatScrolled,
            isContentUnderChat,
            isExpanded,
            resolvedChatContext,
        ],
    );

    const input = useMemo<ChatComposerInput>(() => ({ draft, transcription }), [draft, transcription]);

    return (
        <ChatComposerCallbacksContext.Provider value={callbacks}>
            <ChatComposerActiveContext.Provider value={active}>
                <ChatComposerInputContext.Provider value={input}>
                    <ChatComposerConversationContext.Provider value={conversation}>
                        {children}
                    </ChatComposerConversationContext.Provider>
                </ChatComposerInputContext.Provider>
            </ChatComposerActiveContext.Provider>
        </ChatComposerCallbacksContext.Provider>
    );
};

export const useChatComposerContext = (): ChatComposerCallbacks & ChatComposerActive & ChatComposerInput => ({
    ...useChatComposerCallbacks(),
    ...useChatComposerActive(),
    ...useChatComposerInput(),
});
